import React, { createContext, useState, useEffect, useContext } from "react";
import jwtDecode from "jwt-decode";
import Cookies from "universal-cookie";
import Loading from "components/loading";
import { logoutUrl } from "config/auth";
import { getMultilangMessage } from "utils/multilang";
import tokenStorage from "../token-storage";
import authDataStorage from "./context/auth-data-storage";

const cookies = new Cookies();
const { fetchToken, storeToken } = tokenStorage;
const { fetchLocale, storeLocale, fetchLocaleLoadBoolean, storeLocaleLoadBoolean } = authDataStorage;

export const AuthDataContext = createContext(null);

const decodeToken = ({ token, setAuthData }) => {
  try {
    if (token) {
      const user = jwtDecode(token);

      // TODO: Change on each new token version
      if (!user.permissions) {
        storeToken("");
        return;
      }

      const localeLoad = fetchLocaleLoadBoolean() && JSON.parse(fetchLocaleLoadBoolean());

      if (!localeLoad) {
        storeLocale(user.locale);
      } else if (localeLoad && fetchLocale()) {
        user.locale = fetchLocale();
      }

      setAuthData({ user, token });
    }
  } catch (error) {}
};

const AuthDataProvider = ({ children }) => {
  const [authData, setAuthData] = useState(null);

  useEffect(() => {
    const currentToken = fetchToken();
    decodeToken({ token: currentToken, setAuthData });
  }, []);

  const onLogout = () => {
    storeToken("");
    storeLocaleLoadBoolean(false);
    // Get Keycloak ID token for proper logout with v26
    const idToken = cookies.get("keycloakIdToken");
    // Clean up cookies
    cookies.remove("authToken", { path: "/" });
    cookies.remove("keycloakIdToken", { path: "/" });
    window.location.assign(logoutUrl(idToken));
  };

  // Reloading the application we ensure that the backend token is refreshed
  const onRefreshToken = () => window.location.reload();

  if (!authData) {
    return <Loading message={getMultilangMessage("loadingUserData")} />;
  }

  const handleStoreLocale = value => {
    storeLocale(value);
    storeLocaleLoadBoolean(true);

    const newAuthData = { ...authData };
    newAuthData.user.locale = value;
    setAuthData(newAuthData);
  };

  return (
    <AuthDataContext.Provider
      value={{
        ...authData,
        loggedIn: Boolean(authData?.user),
        hasAdminPrivileges: authData?.user?.hasAdminPrivileges,
        isGlobalAdmin: authData?.user?.isGlobalAdmin,
        isPartner: authData?.user?.isPartner,
        locale: authData?.user?.locale,
        onLocaleChange: handleStoreLocale,
        onLogout,
        onRefreshToken,
        userTheme: authData?.user?.userTheme,
        manageableUserRoles: authData?.user?.manageableUserRoles,
      }}
    >
      {children}
    </AuthDataContext.Provider>
  );
};

export const useAuthDataContext = () => {
  const context = useContext(AuthDataContext);
  if (context === undefined) {
    throw new Error("useAuthDataContext must be used within a AuthDataProvider");
  }
  return context;
};

export default AuthDataProvider;
